import { Box, useTheme } from "@mui/material";
import { Profile } from "./types";
import OrgGraphVis from "org-graph/OrgGraphVis";
import { useAuth } from "react-oidc-context";
import { useOutletContext } from "react-router-dom";
import { CompanyAssessmentOutletContext } from "./CompanyAssessment";
import { useToast } from "../../components/Toast";
import { ApiError } from "../../lib/api";
import { enableGraphMonitoring } from "../GraphMonitoring/monitoringApi";
import { deleteNodeComment, saveNodeComment } from "./nodeCommentsApi";
import type { NodeCommentTarget } from "./nodeCommentsApi";
import { useTranslation } from "react-i18next";

interface OrgGraphCardProps {
  profile: Profile;
  onHiddenNodesChange: (hidden: Set<string>) => void;
  legendLeftOffset?: string;
  selectedDate: string | null;
  onDateChange: (date: string | null) => void;
  onGraphUpdate: (nodes: any[], edges: any[]) => void;
  geographicCompanyIds?: ReadonlySet<string> | null;
  enableIvoLicenseOverlay?: boolean;
  enableCarEntityList?: boolean;
  enableVehicleExpansion?: boolean;
  enableTenantCases?: boolean;
  onMonitoringStateChanged?: (orgNumber: string) => void;
}

export default function OrgGraphCard({ 
  profile, 
  onHiddenNodesChange, 
  legendLeftOffset = "0px", 
  selectedDate, 
  onDateChange, 
  onGraphUpdate,
  geographicCompanyIds,
  enableIvoLicenseOverlay = false,
  enableCarEntityList = false,
  enableVehicleExpansion = false,
  enableTenantCases = false,
  onMonitoringStateChanged,
}: OrgGraphCardProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const nodes = profile.graphData.nodes;
  const relationships = profile.graphData.relationships;
  const graphMetaData = profile.graphData.metadata;
  const showFilterButtonsValue = true;

  const auth = useAuth();
  const { orgGraphRef, isFullscreen, setIsFullscreen, topBarContent } = useOutletContext<CompanyAssessmentOutletContext>();
  
  const muiTheme = useTheme();
  const isDarkMode = muiTheme.palette.mode === 'dark';

  const handleMonitorCompany = async (orgNumber: string, displayName?: string) => {
    try {
      await enableGraphMonitoring(orgNumber, displayName);
      if (orgNumber === profile.orgNumber) {
        onMonitoringStateChanged?.(orgNumber);
      }
      toast.success(t('graph.messages.monitoringEnabled', { name: displayName || orgNumber }));
    } catch (error) {
      const message =
        error instanceof ApiError && typeof error.body === 'string' && error.body.trim()
          ? error.body
          : error instanceof Error
            ? error.message
            : t('graph.errors.enableMonitoring');

      toast.error(message);
      throw error;
    }
  };

  const handleSaveCompanyComment = async (target: NodeCommentTarget, commentMarkdown: string) => {
    try {
      const comment = await saveNodeComment(target, commentMarkdown);
      toast.success(t('graph.messages.commentUpdated', { name: target.displayLabel }));
      return comment;
    } catch (error) {
      const message =
        error instanceof ApiError && typeof error.body === 'string' && error.body.trim()
          ? error.body
          : error instanceof Error
            ? error.message
            : t('graph.errors.saveComment');

      toast.error(message);
      throw error instanceof Error ? error : new Error(message);
    }
  };

  const handleDeleteCompanyComment = async (target: NodeCommentTarget) => {
    try {
      await deleteNodeComment(target);
      toast.success(t('graph.messages.commentRemoved', { name: target.displayLabel }));
    } catch (error) {
      const message =
        error instanceof ApiError && typeof error.body === 'string' && error.body.trim()
          ? error.body
          : error instanceof Error
            ? error.message
            : t('graph.errors.deleteComment');

      toast.error(message);
      throw error instanceof Error ? error : new Error(message);
    }
  };

  return (
    <Box id="org-graph-card" sx={{ width: '100%', height: '100%' }}>
      <OrgGraphVis
        ref={orgGraphRef}
        nodes={nodes}
        relationships={relationships}
        orgNumber={profile.orgNumber}
        graphMetaData={graphMetaData}
        showFilterButtons={showFilterButtonsValue}
        getAccessToken={() => auth.user?.access_token}
        hiddenStateStack={profile.hiddenStateStack}
        selectedGraphTypes={profile.selectedGraphTypes}
        showBadPathsOnly={profile.showBadPathsOnly}
        topPanelLeftSpacerWidth={legendLeftOffset}
        legendLeftOffset={legendLeftOffset}
        isFullscreen={isFullscreen}
        onToggleFullscreen={setIsFullscreen}
        isDarkMode={isDarkMode}
        topBarContent={topBarContent}
        onHiddenNodesChange={onHiddenNodesChange}
        selectedDate={selectedDate}
        onDateChange={onDateChange}
        onGraphUpdate={onGraphUpdate}
        geographicCompanyIds={geographicCompanyIds}
        onMonitorCompany={handleMonitorCompany}
        enableIvoLicenseOverlay={enableIvoLicenseOverlay}
        enableCarEntityList={enableCarEntityList}
        enableVehicleExpansion={enableVehicleExpansion}
        enableTenantCases={enableTenantCases}
        enableCompanyComments
        onSaveCompanyComment={handleSaveCompanyComment}
        onDeleteCompanyComment={handleDeleteCompanyComment}
      />
    </Box>
  );
}
